import React, { useEffect, useState } from 'react';
import { UserCheck } from 'lucide-react';
import { EdiService, formatCents, formatEdiDate } from '../../../services/billing/edi.service';
import type { EdiWorkItem } from '../../../dtos/billing/edi.dto';
import { usePermission } from '../../../hooks/usePermission';
import { BILLING_WORK, EdiGate, ErrorNote, SimulatedBadge, WORK_KIND_LABEL, errorText, useCurrentUserId } from './ediUi';

interface AssignedToMeProps {
  /** Only this patient's items (patient home). */
  patientId?: string;
  onOpen?: (workItemId: string) => void;
  limit?: number;
}

const ACTIVE_STATES = new Set(['OPEN', 'IN_PROGRESS', 'WAITING_AGENCY']);

const AssignedToMeInner: React.FC<AssignedToMeProps> = ({ patientId, onOpen, limit = 5 }) => {
  const userId = useCurrentUserId();
  const [items, setItems] = useState<EdiWorkItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    EdiService.listWorklist(patientId ? { patientId } : {})
      .then((page) => {
        if (cancelled) return;
        // The worklist has no assignee filter (reported); filter on the client
        setItems(page.items.filter((i) => i.assigneeUserId === userId && ACTIVE_STATES.has(i.state)));
      })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Failed to load your exceptions')); });
    return () => { cancelled = true; };
  }, [patientId, userId]);

  return (
    <section aria-label="Assigned to me" className="rounded-lg border border-slate-200 bg-white p-3">
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
        <UserCheck size={16} /> Assigned to me {items && <span className="text-slate-500">({items.length})</span>}
      </h3>
      <ErrorNote message={error} />
      {items && items.length === 0 && <p className="text-sm text-slate-500">Nothing assigned to you.</p>}
      {items && items.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {items.slice(0, limit).map((i) => (
            <li key={i.workItemId}>
              <button
                type="button"
                onClick={() => onOpen?.(i.workItemId)}
                className="w-full py-2 text-left text-sm hover:bg-slate-50"
              >
                <span className="font-medium text-slate-800">{WORK_KIND_LABEL[i.kind] || i.kind}</span>
                {i.patientName && <span className="text-slate-600"> · {i.patientName}</span>}
                {i.simulated && <SimulatedBadge className="ml-1" />}
                <span className="block break-words text-xs text-slate-500">
                  {i.reason.text || i.reason.code}
                  {i.amountCents !== null ? ` · ${formatCents(i.amountCents)}` : ''}
                  {i.deadline ? ` · due ${formatEdiDate(i.deadline)}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const WorkGate: React.FC<AssignedToMeProps> = (props) => {
  const canWork = usePermission(BILLING_WORK);
  return canWork ? <AssignedToMeInner {...props} /> : null;
};

/**
 * Exceptions assigned to the signed-in user (dashboard; pass patientId for one patient).
 * Renders nothing, and sends no request, when billingEdi is off or the user lacks billing:work.
 */
const AssignedToMe: React.FC<AssignedToMeProps> = (props) => (
  <EdiGate>
    <WorkGate {...props} />
  </EdiGate>
);

export default AssignedToMe;
